import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  X,
  Send,
  Download,
  Trash2,
  ShieldOff,
  ShieldAlert,
  RotateCcw,
} from "lucide-react";
import moment from "moment";
import { Modal } from "../../../components/ui";
import { useTicketsStore } from "./use-tickets-store";
import * as api from "./tickets-api";
import {
  TICKET_STATUS,
  TICKET_PRIORITY,
  getStatusText,
  getPriorityText,
  getSectionLabel,
} from "./tickets-types";
import type { ITicketMessage, ITicketFile } from "./tickets-types";

// ─── Layout ───────────────────────────────────────────────────────────────────

const Wrapper = styled.div`
  padding: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #202020;
  margin: 0 0 12px;
  line-height: 1.3;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Badge = styled.span<{ $bg: string; $color: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

const MetaText = styled.span`
  font-size: 13px;
  color: #999;
`;

const Dot = styled.span`
  font-size: 13px;
  color: #ccc;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 4px;
  margin-top: 2px;
  &:hover {
    color: #202020;
  }
`;

// ─── Author ───────────────────────────────────────────────────────────────────

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
  padding: 16px 20px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
`;

const Avatar = styled.div<{ $bg: string; $color: string; $size?: number }>`
  width: ${(p) => p.$size || 44}px;
  height: ${(p) => p.$size || 44}px;
  border-radius: 50%;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: ${(p) => (p.$size ? Math.round(p.$size * 0.35) : 16)}px;
  flex-shrink: 0;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AuthorName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #202020;
`;

const AuthorEmail = styled.div`
  font-size: 13px;
  color: #666;
`;

const AuthorMeta = styled.div`
  margin-left: auto;
  font-size: 13px;
  color: #999;
`;

// ─── Section Label ────────────────────────────────────────────────────────────

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

// ─── Description ──────────────────────────────────────────────────────────────

const DescCard = styled.div`
  padding: 18px 20px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 15px;
  color: #202020;
  white-space: pre-wrap;
  line-height: 1.6;
`;

const FilesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 24px;
`;

const FileLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  font-size: 13px;
  color: #9b59b6;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #ede7f6;
  }
`;

const FileSize = styled.span`
  color: #999;
  font-size: 12px;
`;

// ─── Messages ─────────────────────────────────────────────────────────────────

const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: #202020;
  margin: 0 0 16px;
`;

const MessagesWrap = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 4px;
`;

const MessageCard = styled.div<{ $type: string }>`
  padding: 14px 18px;
  border-radius: 10px;
  background: ${(p) => (p.$type === "support" ? "#e3f2fd" : "#fafafa")};
  border: 1px solid ${(p) => (p.$type === "support" ? "#bbdefb" : "#f0f0f0")};
  position: relative;
`;

const DeletedMessageCard = styled(MessageCard)`
  opacity: 0.4;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const MessageAuthor = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #202020;
`;

const MessageTime = styled.span`
  font-size: 12px;
  color: #999;
`;

const MessageContent = styled.div`
  font-size: 14px;
  color: #202020;
  white-space: pre-wrap;
  line-height: 1.6;
`;

const MessageActions = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 6px;
`;

const SmallIconBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #ccc;
  padding: 2px;
  transition: color 0.15s;
  &:hover {
    color: #f44336;
  }
`;

const RestoreBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #4caf50;
  padding: 2px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    color: #2e7d32;
  }
`;

const EmptyMessages = styled.div`
  text-align: center;
  padding: 32px 20px;
  color: #999;
  font-size: 14px;
`;

// ─── Reply Form ───────────────────────────────────────────────────────────────

const ReplySection = styled.div`
  margin-bottom: 24px;
`;

const ReplyTextarea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  outline: none;
  font-family: inherit;
  line-height: 1.5;
  box-sizing: border-box;
  &:focus {
    border-color: #9b59b6;
  }
`;

const ReplyActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;

const SendBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: #9b59b6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #8e44ad;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ─── Footer ───────────────────────────────────────────────────────────────────

const Divider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin-bottom: 20px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

const FooterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FooterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  background: white;
  cursor: pointer;
`;

const FooterLabel = styled.span`
  font-size: 13px;
  color: #666;
  font-weight: 500;
`;

const Spacer = styled.div`
  flex: 1;
`;

const ActionBtn = styled.button<{ $borderColor: string; $textColor: string; $hoverBg: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid ${(p) => p.$borderColor};
  border-radius: 8px;
  background: transparent;
  color: ${(p) => p.$textColor};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: ${(p) => p.$hoverBg};
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const statusColors: Record<string, { bg: string; color: string }> = {
  [TICKET_STATUS.OPEN]: { bg: "#fff3e0", color: "#ff9800" },
  [TICKET_STATUS.WAITING]: { bg: "#e3f2fd", color: "#1565c0" },
  [TICKET_STATUS.RESOLVED]: { bg: "#e8f5e9", color: "#4caf50" },
  [TICKET_STATUS.CLOSED]: { bg: "#ffebee", color: "#f44336" },
};

const priorityColors: Record<string, { bg: string; color: string }> = {
  [TICKET_PRIORITY.HIGH]: { bg: "#ffebee", color: "#f44336" },
  [TICKET_PRIORITY.NORMAL]: { bg: "#fff3e0", color: "#ff9800" },
  [TICKET_PRIORITY.LOW]: { bg: "#e8f5e9", color: "#4caf50" },
};

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function handleDownload(file: ITicketFile) {
  api.downloadFile(file.uuid).then((blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.file_name;
    a.click();
    URL.revokeObjectURL(url);
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ViewTicketModal: React.FC = () => {
  const { selectedTicket, closeViewModal, openDeleteModal, fetch, fetchStats } =
    useTicketsStore();

  const [messages, setMessages] = useState<ITicketMessage[]>([]);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(selectedTicket?.status || "");
  const [priority, setPriority] = useState(selectedTicket?.priority || "");

  const ticketUuid = selectedTicket?.uuid;

  useEffect(() => {
    if (ticketUuid) {
      api.getMessages(ticketUuid).then(setMessages);
    }
  }, [ticketUuid]);

  if (!selectedTicket) return null;

  const ticket = selectedTicket;

  const loadMessages = () => {
    api.getMessages(ticket.uuid).then(setMessages);
  };

  const handleSend = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await api.addSupportMessage(ticket.uuid, replyText.trim());
      setReplyText("");
      loadMessages();
      fetch();
      fetchStats();
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setStatus(newStatus);
    await api.updateTicketStatus(ticket.uuid, newStatus);
    fetch();
    fetchStats();
  };

  const handlePriorityChange = async (newPriority: string) => {
    setPriority(newPriority);
    await api.updateTicketPriority(ticket.uuid, newPriority);
    fetch();
    fetchStats();
  };

  const handleBlock = async () => {
    if (ticket.is_blocked) {
      await api.unblockTicket(ticket.uuid);
    } else {
      const reason = prompt("Причина блокировки:");
      if (!reason) return;
      await api.blockTicket(ticket.uuid, reason);
    }
    fetch();
    fetchStats();
  };

  const handleDeleteMessage = async (msgUuid: string) => {
    const reason = prompt("Причина удаления сообщения:");
    if (!reason) return;
    await api.deleteMessage(msgUuid, reason);
    loadMessages();
  };

  const handleRestoreMessage = async (msgUuid: string) => {
    await api.restoreMessage(msgUuid);
    loadMessages();
  };

  const handleRestore = async () => {
    await api.restoreTicket(ticket.uuid);
    fetch();
    fetchStats();
    closeViewModal();
  };

  const sc = statusColors[status] || { bg: "#f5f5f5", color: "#999" };
  const pc = priorityColors[priority] || { bg: "#f5f5f5", color: "#999" };
  const authorDisplay = ticket.author_login || ticket.author_email || "—";

  return (
    <Modal handleClose={closeViewModal} contentStyle={{ width: 900 }}>
      <Wrapper>
        {/* Заголовок */}
        <Header>
          <HeaderLeft>
            <Title>{ticket.title}</Title>
            <MetaRow>
              <Badge $bg={sc.bg} $color={sc.color}>
                {getStatusText(status)}
              </Badge>
              <Badge $bg={pc.bg} $color={pc.color}>
                {getPriorityText(priority)}
              </Badge>
              <Dot>·</Dot>
              <MetaText>{getSectionLabel(ticket.section)}</MetaText>
              <Dot>·</Dot>
              <MetaText>
                {moment(ticket.created_at).format("DD.MM.YYYY HH:mm")}
              </MetaText>
              {ticket.is_blocked && (
                <Badge $bg="#fff3e0" $color="#ff9800">
                  Заблокирован
                </Badge>
              )}
              {ticket.deleted_at && (
                <Badge $bg="#ffebee" $color="#f44336">
                  Удалён
                </Badge>
              )}
            </MetaRow>
          </HeaderLeft>
          <CloseBtn onClick={closeViewModal}>
            <X size={22} />
          </CloseBtn>
        </Header>

        {/* Автор */}
        <AuthorRow>
          <Avatar $bg="#ede7f6" $color="#9b59b6">
            {getInitials(authorDisplay)}
          </Avatar>
          <AuthorInfo>
            <AuthorName>{authorDisplay}</AuthorName>
            {ticket.author_email && (
              <AuthorEmail>{ticket.author_email}</AuthorEmail>
            )}
          </AuthorInfo>
          <AuthorMeta>
            {ticket.messages_count ?? 0} сообщ. · {ticket.files?.length ?? 0} файлов
          </AuthorMeta>
        </AuthorRow>

        {/* Описание */}
        <SectionLabel>Описание</SectionLabel>
        <DescCard>{ticket.description}</DescCard>

        {/* Файлы тикета */}
        {ticket.files && ticket.files.length > 0 && (
          <>
            <SectionLabel>Файлы</SectionLabel>
            <FilesRow>
              {ticket.files.map((file) => (
                <FileLink key={file.uuid} onClick={() => handleDownload(file)}>
                  <Download size={14} />
                  {file.file_name}
                  {file.file_size && (
                    <FileSize>{formatFileSize(file.file_size)}</FileSize>
                  )}
                </FileLink>
              ))}
            </FilesRow>
          </>
        )}

        {/* Сообщения */}
        <SectionTitle>Сообщения ({messages.length})</SectionTitle>
        <MessagesWrap>
          {messages.map((msg) => {
            const MsgCard = msg.deleted_at ? DeletedMessageCard : MessageCard;
            const msgType = msg.is_from_customer ? "customer" : "support";
            const authorName = msg.is_from_customer ? "Клиент" : "Поддержка";
            return (
              <MsgCard key={msg.uuid} $type={msgType}>
                <MessageHeader>
                  <Avatar
                    $bg={!msg.is_from_customer ? "#e3f2fd" : "#ede7f6"}
                    $color={!msg.is_from_customer ? "#1565c0" : "#9b59b6"}
                    $size={28}
                  >
                    {getInitials(authorName)}
                  </Avatar>
                  <MessageAuthor>{authorName}</MessageAuthor>
                  <MessageTime>
                    {moment(msg.created_at).format("DD.MM.YYYY HH:mm")}
                  </MessageTime>
                  {msg.deleted_at && (
                    <MetaText style={{ color: "#f44336" }}>удалено</MetaText>
                  )}
                </MessageHeader>
                <MessageContent>{msg.message}</MessageContent>
                <MessageActions>
                  {msg.deleted_at ? (
                    <RestoreBtn onClick={() => handleRestoreMessage(msg.uuid)}>
                      <RotateCcw size={13} />
                      Восстановить
                    </RestoreBtn>
                  ) : (
                    <SmallIconBtn
                      onClick={() => handleDeleteMessage(msg.uuid)}
                      title="Удалить сообщение"
                    >
                      <Trash2 size={15} />
                    </SmallIconBtn>
                  )}
                </MessageActions>
              </MsgCard>
            );
          })}
          {messages.length === 0 && (
            <EmptyMessages>Сообщений пока нет</EmptyMessages>
          )}
        </MessagesWrap>

        {/* Форма ответа */}
        {!ticket.deleted_at && (
          <ReplySection>
            <SectionLabel>Ответ</SectionLabel>
            <ReplyTextarea
              placeholder="Написать ответ клиенту..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
            />
            <ReplyActions>
              <SendBtn
                onClick={handleSend}
                disabled={sending || !replyText.trim()}
              >
                <Send size={16} />
                Отправить
              </SendBtn>
            </ReplyActions>
          </ReplySection>
        )}

        {/* Действия */}
        <Divider />
        <Footer>
          <FooterGroup>
            <FooterLabel>Статус:</FooterLabel>
            <FooterSelect
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <option value={TICKET_STATUS.OPEN}>Открыт</option>
              <option value={TICKET_STATUS.WAITING}>Ожидание</option>
              <option value={TICKET_STATUS.RESOLVED}>Решён</option>
              <option value={TICKET_STATUS.CLOSED}>Закрыт</option>
            </FooterSelect>
          </FooterGroup>

          <FooterGroup>
            <FooterLabel>Приоритет:</FooterLabel>
            <FooterSelect
              value={priority}
              onChange={(e) => handlePriorityChange(e.target.value)}
            >
              <option value={TICKET_PRIORITY.HIGH}>Высокий</option>
              <option value={TICKET_PRIORITY.NORMAL}>Средний</option>
              <option value={TICKET_PRIORITY.LOW}>Низкий</option>
            </FooterSelect>
          </FooterGroup>

          <Spacer />

          <ActionBtn
            $borderColor="#ff9800"
            $textColor="#ff9800"
            $hoverBg="#fff3e0"
            onClick={handleBlock}
          >
            {ticket.is_blocked ? (
              <>
                <ShieldOff size={15} />
                Разблокировать
              </>
            ) : (
              <>
                <ShieldAlert size={15} />
                Заблокировать
              </>
            )}
          </ActionBtn>

          {ticket.deleted_at ? (
            <ActionBtn
              $borderColor="#4caf50"
              $textColor="#4caf50"
              $hoverBg="#e8f5e9"
              onClick={handleRestore}
            >
              <RotateCcw size={15} />
              Восстановить
            </ActionBtn>
          ) : (
            <ActionBtn
              $borderColor="#f44336"
              $textColor="#f44336"
              $hoverBg="#ffebee"
              onClick={() => {
                closeViewModal();
                openDeleteModal(ticket);
              }}
            >
              <Trash2 size={15} />
              Удалить
            </ActionBtn>
          )}
        </Footer>
      </Wrapper>
    </Modal>
  );
};
